import { useEffect, useState } from 'react'
import { addUpdateHandler } from '../../utils'
import { PaymentImport } from './Import'
const {
    RoutesMap, useNavigate, useDispatch, useSelector, toast, SessionManager, getConfig,
    Loader, ButtonLoader, paymentList, applyCoupon, createInstituteOrder, verifyInstituteOrder, sidebar,
    CreditCard, ShieldCheck, UserRound, Mail, Phone, IndianRupee, RefreshCw,
    Button, Card, logo,
} = PaymentImport

const loadRazorpayScript = () => {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true)
            return
        }
        const script = document.createElement('script')
        script.src = 'https://checkout.razorpay.com/v1/checkout.js'
        script.onload = () => resolve(true)
        script.onerror = () => resolve(false)
        document.body.appendChild(script)
    })
}

export default function PaymentPage() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const payments = useSelector((state) => state?.paymentList?.data)

    const [loading, setLoading] = useState(true)
    const [paying, setPaying] = useState(false)
    const [couponCode, setCouponCode] = useState('')
    const [couponApplying, setCouponApplying] = useState(false)
    const [appliedCoupon, setAppliedCoupon] = useState(null)

    const user = JSON.parse(SessionManager.getCookie('user') || '{}')
    const userName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'User'
    const instituteBranchId = user?.institute_branch_id || user?.branchUser?.id || user?.instituteBranch?.id
    const paymentMaster = Array.isArray(payments) ? payments[0] : null
    const amount = Number(paymentMaster?.recurring_payment || 0)
    const discountAmount = Number(appliedCoupon?.discount_amount || 0)
    const payableAmount = appliedCoupon?.final_amount != null ? Number(appliedCoupon.final_amount) : amount

    useEffect(() => {
        if (!SessionManager.getCookie('user_token')) {
            navigate(RoutesMap.LOGIN.path)
            return
        }
        const paymentStatus = String(user?.payment_status || '').toLowerCase()
        if (paymentStatus && paymentStatus !== 'pending' && paymentStatus !== 'hold') {
            navigate(RoutesMap.DASHBOARD.path)
            return
        }
        fetchPayment()
    }, [])

    const fetchPayment = () => {
        setLoading(true)
        setAppliedCoupon(null)
        setCouponCode('')
        dispatch(paymentList({ role_id: user?.role_id, navigate })).finally(() => setLoading(false))
    }

    const afterPaymentSuccess = () => {
        const updatedUser = { ...user, payment_status: 'Paid' }
        SessionManager.setCookie('user', JSON.stringify(updatedUser), 3)
        toast.success('Payment successful!', { position: 'bottom-right' })
        dispatch(sidebar({ navigate })).then((res) => {
            if (res?.payload?.status === 200) {
                const permission = res?.payload?.data?.data?.attributes?.map((val) => JSON.parse(val)).flat()
                SessionManager.setSession('permission', JSON.stringify(permission))
                navigate(RoutesMap.DASHBOARD.path)
            } else {
                navigate(RoutesMap.DASHBOARD.path)
            }
            setPaying(false)
        })
    }

    const openRazorpay = async (orderData) => {
        const scriptLoaded = await loadRazorpayScript()
        if (!scriptLoaded) {
            setPaying(false)
            toast.error('Unable to load Razorpay. Please try again.', { position: 'bottom-right' })
            return
        }

        const order = orderData?.data?.data || orderData?.data || {}
        const backendUrl = String(getConfig.BACKEND_URL || '').replace(/\/$/, '')
        if (!backendUrl) {
            setPaying(false)
            toast.error('Payment is not configured. Please contact support.', { position: 'bottom-right' })
            return
        }
        const options = {
            key: getConfig.RAZORPAY_KEY_ID || order?.key || order?.key_id,
            amount: order?.amount || Math.round(payableAmount * 100),
            currency: order?.currency || 'INR',
            name: 'IQB LMS',
            description: 'Registration Fee',
            order_id: order?.order_id || order?.id || order?.razorpay_order_id,
            callback_url: `${backendUrl}/api/v1/razorpay_callback`,
            redirect: true,
            prefill: {
                name: userName,
                email: user?.email || '',
                contact: user?.phone || '',
            },
            theme: { color: '#0d9488' },
            // handler is not called when redirect is true. Razorpay POSTs to callback_url.
            handler: (response) => {
                dispatch(verifyInstituteOrder({
                    razorpay_order_id: response?.razorpay_order_id,
                    razorpay_payment_id: response?.razorpay_payment_id,
                    razorpay_signature: response?.razorpay_signature,
                })).then((res) => {
                    const payload = res?.payload
                    if (payload?.status === 200 || payload?.status === 201) {
                        afterPaymentSuccess()
                    } else {
                        addUpdateHandler({
                            res: payload,
                            navigate,
                            success_toast: 'Payment successful!',
                            wrong_toast: 'Payment verification failed.',
                            navigate_route: '',
                            spinner: setPaying,
                            toggle: () => {},
                        })
                    }
                })
            },
            modal: {
                ondismiss: () => setPaying(false),
            },
        }

        const rzp = new window.Razorpay(options)
        rzp.on('payment.failed', () => {
            setPaying(false)
            toast.error('Payment failed. Please try again.', { position: 'bottom-right' })
        })
        rzp.open()
    }

    const handleApplyCoupon = () => {
        const code = String(couponCode || '').trim()
        if (!code) {
            toast.error('Enter Coupon Code', { position: 'bottom-right' })
            return
        }
        if (!instituteBranchId) {
            toast.error('Institute branch not found.', { position: 'bottom-right' })
            return
        }
        setCouponApplying(true)
        dispatch(applyCoupon({
            payment_type: 'Registration Fee',
            institute_branch_id: Number(instituteBranchId),
            coupon_code: code,
        })).then((res) => {
            const payload = res?.payload
            const data = payload?.data?.data || payload?.data || {}
            if (payload?.status === 200 || payload?.status === 201) {
                setAppliedCoupon(data)
                toast.success('Coupon applied successfully', { position: 'bottom-right' })
                setCouponApplying(false)
            } else {
                setAppliedCoupon(null)
                addUpdateHandler({
                    res: payload,
                    navigate,
                    success_toast: 'Coupon applied successfully',
                    wrong_toast: 'Unable to apply coupon.',
                    navigate_route: '',
                    spinner: setCouponApplying,
                    toggle: () => {},
                })
            }
        })
    }

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null)
        setCouponCode('')
    }

    const handlePayNow = () => {
        if (!instituteBranchId) {
            toast.error('Institute branch not found.', { position: 'bottom-right' })
            return
        }
        setPaying(true)
        const orderPayload = { institute_branch_id: Number(instituteBranchId) }
        if (appliedCoupon?.coupon_id != null) orderPayload.coupon_id = appliedCoupon.coupon_id
        if (appliedCoupon?.coupon_code) orderPayload.coupon_code = appliedCoupon.coupon_code
        dispatch(createInstituteOrder(orderPayload)).then((res) => {
            const payload = res?.payload
            if (payload?.status === 200 || payload?.status === 201) {
                openRazorpay(payload)
            } else {
                addUpdateHandler({
                    res: payload,
                    navigate,
                    success_toast: 'Payment successful!',
                    wrong_toast: 'Unable to initiate payment.',
                    navigate_route: '',
                    spinner: setPaying,
                    toggle: () => {},
                })
            }
        })
    }

    const handleLogout = () => {
        SessionManager.removeCookie('user_token')
        SessionManager.removeCookie('user')
        SessionManager.clearSession()
        navigate(RoutesMap.LOGIN.path)
    }

    return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center p-3" style={{
            background: 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 50%, #f8fafc 100%)',
        }}>
            <div style={{ width: '100%', maxWidth: 520 }}>
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="text-center p-4" style={{ background: 'linear-gradient(120deg, #0f766e, #0e7490)' }}>
                        <img src={logo} alt="logo" style={{ height: 56, filter: 'brightness(0) invert(1)' }} />
                        <h4 className="text-white mt-3 mb-1">Complete Your Payment</h4>
                        <p className="text-white-50 mb-0" style={{ fontSize: 14 }}>
                            Registration fee is pending. Pay to continue using IQB LMS.
                        </p>
                    </div>

                    <Card.Body className="p-4">
                        {loading ? (
                            <Loader />
                        ) : (
                            <>
                                <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded-3" style={{ background: '#f8fafc' }}>
                                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: '#ccfbf1' }}>
                                        <UserRound size={22} color="#0f766e" />
                                    </div>
                                    <div className="flex-fill">
                                        <div className="fw-semibold" style={{ color: '#0f172a' }}>{userName}</div>
                                        <div className="text-muted" style={{ fontSize: 13 }}>
                                            {user?.role_name || 'User'}{user?.user_code ? ` · ${user.user_code}` : ''}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        title="Refresh"
                                        className="btn btn-sm border-0 d-flex align-items-center justify-content-center"
                                        style={{ width: 32, height: 32, background: '#ccfbf1', borderRadius: '50%' }}
                                        onClick={fetchPayment}
                                        disabled={loading}
                                    >
                                        <RefreshCw size={14} color="#0f766e" />
                                    </button>
                                </div>

                                <div className="mb-3 d-flex align-items-center gap-2 text-muted" style={{ fontSize: 14 }}>
                                    <Mail size={16} /> {user?.email || '-'}
                                </div>
                                <div className="mb-4 d-flex align-items-center gap-2 text-muted" style={{ fontSize: 14 }}>
                                    <Phone size={16} /> {user?.phone || '-'}
                                </div>

                                <div className="rounded-3 p-3 mb-3" style={{ border: '1px dashed #99f6e4', background: '#f0fdfa' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-muted" style={{ fontSize: 13 }}>Payment Type</span>
                                        <span className="fw-medium">Registration Fee</span>
                                    </div>
                                    {appliedCoupon ? (
                                        <>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="text-muted" style={{ fontSize: 13 }}>Total Amount</span>
                                                <span className="fw-medium">
                                                    ₹{Number(appliedCoupon.total_amount ?? amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="text-muted" style={{ fontSize: 13 }}>Discount</span>
                                                <span className="fw-medium" style={{ color: '#0f766e' }}>
                                                    - ₹{discountAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        </>
                                    ) : null}
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-muted" style={{ fontSize: 13 }}>Amount Payable</span>
                                        <span className="fw-bold d-flex align-items-center gap-1" style={{ fontSize: 22, color: '#0f766e' }}>
                                            <IndianRupee size={18} />
                                            {payableAmount ? payableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}
                                        </span>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <div className="text-muted mb-2" style={{ fontSize: 13 }}>Coupon Code</div>
                                    <div className="d-flex gap-2">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={couponCode}
                                            disabled={!!appliedCoupon || couponApplying || paying}
                                            onChange={(e) => setCouponCode(e.target.value)}
                                            placeholder="Enter coupon code"
                                        />
                                        {appliedCoupon ? (
                                            <Button variant="outline-secondary" onClick={handleRemoveCoupon} disabled={paying}>
                                                Remove
                                            </Button>
                                        ) : (
                                            <Button
                                                style={{ background: '#0f766e', border: 'none', whiteSpace: 'nowrap' }}
                                                onClick={handleApplyCoupon}
                                                disabled={couponApplying || paying || !instituteBranchId}
                                            >
                                                {couponApplying ? 'Applying...' : 'Apply'}
                                            </Button>
                                        )}
                                    </div>
                                    {appliedCoupon?.coupon_code ? (
                                        <div className="mt-2" style={{ fontSize: 12, color: '#0f766e', fontWeight: 600 }}>
                                            Applied: {appliedCoupon.coupon_code}
                                        </div>
                                    ) : null}
                                </div>

                                <div className="d-flex align-items-center gap-2 mb-3 text-muted" style={{ fontSize: 12 }}>
                                    <ShieldCheck size={16} color="#0f766e" />
                                    Secured by Razorpay. Your payment details are encrypted.
                                </div>

                                <Button
                                    className="w-100 d-flex align-items-center justify-content-center gap-2 border-0"
                                    style={{ background: '#0f766e', height: 44 }}
                                    disabled={paying || !instituteBranchId || !amount}
                                    onClick={handlePayNow}
                                >
                                    {paying ? <ButtonLoader /> : (<><CreditCard size={18} /> Pay Now</>)}
                                </Button>

                                <div className="text-center mt-3">
                                    <button type="button" className="btn btn-link btn-sm text-muted text-decoration-none" onClick={handleLogout}>
                                        Logout
                                    </button>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </div>
    )
}
